import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, Printer, Copy, X, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, RefreshCw, Plus, Calendar,
  Save, List, LayoutGrid, ChevronDown, ChevronUp,
} from "lucide-react";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

const STATUS_OPTIONS = [
  { key: "draft", label: "Phiếu tạm" },
  { key: "completed", label: "Hoàn thành" },
  { key: "cancelled", label: "Đã hủy" },
];

const TIME_OPTIONS = [
  { key: "today", label: "Hôm nay" },
  { key: "7days", label: "7 ngày qua" },
  { key: "this_month", label: "Tháng này" },
  { key: "30days", label: "30 ngày qua" },
  { key: "90days", label: "90 ngày qua" },
  { key: "365days", label: "1 năm qua" },
  { key: "all", label: "Tất cả thời gian" },
  { key: "custom", label: "Lựa chọn khác" },
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      if (dateStr.includes("T")) {
        const parts = dateStr.split("T");
        const dp = parts[0].split("-");
        const tp = (parts[1] || "").split(":").slice(0, 2).join(":");
        return `${dp[2]}/${dp[1]}/${dp[0]} ${tp}`;
      }
      return dateStr;
    }
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return dateStr; }
}

export default function DamageItemsPage() {
  const [searchCode, setSearchCode] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [searchDisposer, setSearchDisposer] = useState("");
  const [searchCreator, setSearchCreator] = useState("");
  const [timeFilter, setTimeFilter] = useState("30days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilters, setStatusFilters] = useState(["draft", "completed"]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [showFilter, setShowFilter] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);

  const queryParams = useMemo(() => ({
    search: searchCode || undefined,
    search_product: searchProduct || undefined,
    search_disposer: searchDisposer || undefined,
    search_creator: searchCreator || undefined,
    time_filter: timeFilter,
    date_from: timeFilter === "custom" ? dateFrom : undefined,
    date_to: timeFilter === "custom" ? dateTo : undefined,
    status: statusFilters.length > 0 ? statusFilters.join(",") : undefined,
    page, page_size: pageSize,
  }), [searchCode, searchProduct, searchDisposer, searchCreator, timeFilter, dateFrom, dateTo, statusFilters, page, pageSize]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["damage-items", queryParams],
    queryFn: () => api.get("/damage-items", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const items = data?.damage_items || [];
  const totalCount = data?.total || 0;
  const summary = data?.summary || {};
  const totalPages = Math.ceil(totalCount / pageSize);

  const toggleStatus = (key) => { setStatusFilters(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]); setPage(1); };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/damage-items/sync", null, { params: { days: 30 } });
      toast.success(res.data.message || "Đồng bộ thành công");
      refetch();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncing(false); }
  };

  const handleCancel = async (id) => {
    try { await api.post(`/damage-items/${id}/cancel`); toast.success("Đã hủy phiếu"); refetch(); setCancelDialog(null); setExpandedRow(null); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  const handleCopy = async (id) => {
    try { const res = await api.post(`/damage-items/${id}/copy`); toast.success(res.data.message); refetch(); }
    catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
  };

  const handlePrint = (item) => {
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>Phiếu xuất hủy ${item.code}</title>
      <style>body{font-family:sans-serif;padding:20px;max-width:600px;margin:0 auto}table{width:100%;border-collapse:collapse}td,th{padding:6px;border:1px solid #ddd;text-align:left}.right{text-align:right}h2{text-align:center}</style></head>
      <body><h2>KARINOX Arena</h2><p style="text-align:center">PHIẾU XUẤT HỦY</p><hr/>
      <p><b>Mã phiếu:</b> ${item.code}</p><p><b>Ngày:</b> ${formatDateTime(item.created_date)}</p>
      <p><b>Người xuất hủy:</b> ${item.disposer_name}</p><p><b>Ghi chú:</b> ${item.note || "-"}</p><hr/>
      <table><tr><th>Mã hàng</th><th>Tên hàng</th><th class="right">SL hủy</th><th class="right">Giá vốn</th><th class="right">Giá trị hủy</th></tr>
      ${(item.details||[]).map(d=>`<tr><td>${d.product_code}</td><td>${d.product_name}</td><td class="right">${d.quantity}</td><td class="right">${formatVND(d.cost_price)}</td><td class="right">${formatVND(d.damage_value)}</td></tr>`).join("")}
      </table><hr/><p><b>Tổng giá trị hủy: ${formatVND(item.total_value)}</b></p></body></html>`);
    win.document.close(); win.print();
  };

  const handleExport = () => {
    const headers = ["Mã xuất hủy","Thời gian","Chi nhánh","Tổng giá trị","Ghi chú","Trạng thái"];
    const rows = items.map(i => [i.code, formatDateTime(i.created_date), i.branch_name, i.total_value, i.note, i.status_label]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `phieu_xuat_huy_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("Đã xuất file CSV");
  };

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="damage-items-page">
      {/* Left Sidebar */}
      <aside className={sidebarClasses(showFilter)}>
        <MobileFilterClose setShowFilter={setShowFilter} />
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Tìm kiếm<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Theo mã xuất hủy" value={searchCode} onChange={e=>{setSearchCode(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-damage-code" />
          <Input placeholder="Theo mã, tên hàng" value={searchProduct} onChange={e=>{setSearchProduct(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-damage-product" />
          <Input placeholder="Theo người xuất hủy" value={searchDisposer} onChange={e=>{setSearchDisposer(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-damage-disposer" />
          <Input placeholder="Theo người tạo" value={searchCreator} onChange={e=>{setSearchCreator(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-damage-creator" />
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Thời gian<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter!=="custom"} onChange={()=>setTimeFilter("7days")} className="accent-blue-600" />
              <select value={timeFilter==="custom"?"7days":timeFilter} onChange={e=>{setTimeFilter(e.target.value);setPage(1);}} className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 flex-1" data-testid="damage-time-filter">
                {TIME_OPTIONS.filter(t=>t.key!=="custom").map(t=>(<option key={t.key} value={t.key} className="bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200">{t.label}</option>))}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter==="custom"} onChange={()=>setTimeFilter("custom")} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Lựa chọn khác</span><Calendar className="w-4 h-4 text-gray-400" />
            </label>
            {timeFilter==="custom"&&(<div className="pl-6 space-y-1"><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="text-xs" /><Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="text-xs" /></div>)}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Trạng thái<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            {STATUS_OPTIONS.map(opt=>(<label key={opt.key} className="flex items-center gap-2 cursor-pointer"><Checkbox checked={statusFilters.includes(opt.key)} onCheckedChange={()=>toggleStatus(opt.key)} data-testid={`damage-status-${opt.key}`} /><span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span></label>))}
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">Số bản ghi:
            <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}} className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 text-sm" data-testid="damage-page-size">
              <option value={10}>10</option><option value={15}>15</option><option value={20}>20</option><option value={50}>50</option>
            </select>
          </label>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="damage-title">Phiếu xuất hủy</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={()=>setCreateDialog(true)} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-create-damage"><Plus className="w-4 h-4" /> Tạo phiếu</Button>
            <Button onClick={handleSync} variant="outline" className="text-sm gap-1" disabled={syncing} data-testid="btn-sync-damage"><RefreshCw className={`w-4 h-4 ${syncing?"animate-spin":""}`} />{syncing?"Đang đồng bộ...":"Đồng bộ KiotViet"}</Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-export-damage"><Download className="w-4 h-4" /> Xuất file</Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button className="p-1.5 bg-blue-600 text-white"><List className="w-4 h-4" /></button>
              <button className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="damage-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Mã xuất hủy</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Thời gian</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Chi nhánh</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Tổng giá trị</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Ghi chú</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Trạng thái</th>
              </tr>
              <tr className="border-b border-gray-200 dark:border-slate-600 bg-blue-50/50 dark:bg-blue-900/20">
                <td colSpan={3}></td>
                <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300">{formatVND(summary.total_value||0)}</td>
                <td colSpan={2}></td>
              </tr>
            </thead>
            <tbody>
              {isLoading?(<tr><td colSpan={6} className="text-center py-10 text-gray-400">Đang tải...</td></tr>):items.length===0?(<tr><td colSpan={6} className="text-center py-10 text-gray-400">Không có phiếu xuất hủy nào</td></tr>):(
                items.map(item=>(
                  <React.Fragment key={item.id}>
                    <tr className={`border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${expandedRow===item.id?"bg-blue-50 dark:bg-blue-900/30 font-medium":"hover:bg-gray-50 dark:hover:bg-slate-800/50"}`} onClick={()=>setExpandedRow(expandedRow===item.id?null:item.id)} data-testid={`damage-row-${item.code}`}>
                      <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200">{item.code}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{formatDateTime(item.created_date)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.branch_name}</td>
                      <td className="px-3 py-2.5 text-right text-gray-800 dark:text-slate-200">{formatVND(item.total_value)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400 max-w-[200px] truncate">{item.note}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.status_label}</td>
                    </tr>
                    {expandedRow===item.id&&(
                      <tr className="bg-blue-50/30 dark:bg-blue-900/10"><td colSpan={6} className="p-0">
                        <DamageDetail item={item} onPrint={()=>handlePrint(item)} onCopy={()=>handleCopy(item.id)} onCancel={()=>setCancelDialog(item)} onExport={handleExport} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={()=>setPage(1)} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>{let pn;if(totalPages<=5)pn=i+1;else if(page<=3)pn=i+1;else if(page>=totalPages-2)pn=totalPages-4+i;else pn=page-2+i;return(<button key={pn} onClick={()=>setPage(pn)} className={`w-8 h-8 rounded text-sm ${page===pn?"bg-blue-600 text-white":"hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}>{pn}</button>);})}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={()=>setPage(totalPages)} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="damage-pagination-info">Hiển thị {totalCount>0?(page-1)*pageSize+1:0} - {Math.min(page*pageSize,totalCount)} trên tổng số {totalCount} phiếu xuất hủy</span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      <Dialog open={!!cancelDialog} onOpenChange={()=>setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Xác nhận hủy phiếu</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 dark:text-slate-400">Bạn có chắc muốn hủy phiếu <strong>{cancelDialog?.code}</strong>?</p>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={()=>setCancelDialog(null)}>Đóng</Button><Button variant="destructive" onClick={()=>handleCancel(cancelDialog?.id)} data-testid="confirm-cancel-damage">Hủy phiếu</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateDamageDialog open={createDialog} onClose={()=>setCreateDialog(false)} onSuccess={()=>{setCreateDialog(false);refetch();}} />
    </div>
  );
}

function DamageDetail({ item, onPrint, onCopy, onCancel, onExport }) {
  return (
    <div className="px-6 py-4 border-t border-blue-100 dark:border-blue-800" data-testid="damage-detail-panel">
      <div className="flex gap-4 border-b border-gray-200 dark:border-slate-700 mb-4">
        <button className="pb-2 px-1 text-sm font-medium border-b-2 border-blue-600 text-blue-600">Thông tin</button>
      </div>
      <div className="grid grid-cols-2 gap-x-12 gap-y-3 mb-6">
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Mã xuất hủy:</span><span className="text-sm font-medium text-gray-800 dark:text-slate-200">{item.code}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Trạng thái:</span><span className={`text-sm font-medium ${item.status==="completed"?"text-green-600":item.status==="cancelled"?"text-red-600":"text-yellow-600"}`}>{item.status_label}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Thời gian:</span><span className="text-sm text-gray-700 dark:text-slate-300">{formatDateTime(item.created_date)}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Chi nhánh:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.branch_name}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người xuất hủy:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.disposer_name||"-"}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người tạo:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.creator_name||"-"}</span></div>
        <div className="flex items-center gap-3 col-span-2"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Ghi chú:</span><span className="text-sm text-gray-500 dark:text-slate-400 italic">{item.note||"Không có"}</span></div>
      </div>

      <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-slate-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Mã hàng hóa</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Tên hàng</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">SL hủy</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Giá vốn</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Giá trị hủy</th>
            </tr>
          </thead>
          <tbody>
            {(item.details||[]).map((d,idx)=>(<tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
              <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{d.product_code}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{d.product_name}</td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{d.quantity}</td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.cost_price)}</td>
              <td className="px-3 py-2 text-right font-medium text-gray-800 dark:text-slate-200">{formatVND(d.damage_value)}</td>
            </tr>))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mb-4">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Tổng SL hủy:</span><span className="font-medium">{item.total_quantity}</span></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Tổng mặt hàng:</span><span className="font-medium">{item.total_items}</span></div>
          <div className="flex justify-between gap-8 pt-1 border-t border-gray-200 dark:border-slate-700"><span className="font-semibold">Tổng giá trị hủy:</span><span className="font-bold text-red-600 dark:text-red-400">{formatVND(item.total_value)}</span></div>
        </div>
      </div>

      <div className="flex justify-center gap-2 pt-3 border-t border-gray-200 dark:border-slate-700">
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1" data-testid="btn-save-damage"><Save className="w-3 h-3" /> Lưu</Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onPrint} data-testid="btn-print-damage"><Printer className="w-3 h-3" /> In</Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onExport} data-testid="btn-export-damage-detail"><Download className="w-3 h-3" /> Xuất file</Button>
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" onClick={onCopy} data-testid="btn-copy-damage"><Copy className="w-3 h-3" /> Sao chép</Button>
        <Button size="sm" variant="destructive" className="text-xs gap-1" onClick={onCancel} data-testid="btn-cancel-damage"><X className="w-3 h-3" /> Hủy bỏ</Button>
      </div>
    </div>
  );
}

function CreateDamageDialog({ open, onClose, onSuccess }) {
  const [note, setNote] = useState("");
  const [disposerName, setDisposerName] = useState("");
  const [items, setItems] = useState([{ product_code: "", product_name: "", quantity: 1, cost_price: 0 }]);
  const [submitting, setSubmitting] = useState(false);

  const addItem = () => setItems([...items, { product_code: "", product_name: "", quantity: 1, cost_price: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_,i)=>i!==idx));
  const updateItem = (idx, field, value) => { const n=[...items]; n[idx]={...n[idx],[field]:value}; setItems(n); };

  const handleSubmit = async () => {
    if (items.every(i => !i.product_name)) { toast.error("Vui lòng thêm ít nhất 1 sản phẩm"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/damage-items", { note, disposer_name: disposerName, items: items.filter(i=>i.product_name) });
      toast.success(res.data.message); setNote(""); setDisposerName("");
      setItems([{ product_code: "", product_name: "", quantity: 1, cost_price: 0 }]);
      onSuccess();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tạo phiếu xuất hủy mới</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Người xuất hủy</label><Input placeholder="Tên người xuất hủy" value={disposerName} onChange={e=>setDisposerName(e.target.value)} data-testid="create-damage-disposer" /></div>
            <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Ghi chú</label><Input placeholder="Lý do xuất hủy..." value={note} onChange={e=>setNote(e.target.value)} data-testid="create-damage-note" /></div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Danh sách hàng hủy</label>
              <Button size="sm" variant="outline" onClick={addItem} className="text-xs gap-1"><Plus className="w-3 h-3" /> Thêm</Button>
            </div>
            <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-slate-700"><tr><th className="px-2 py-1.5 text-left text-xs">Mã hàng</th><th className="px-2 py-1.5 text-left text-xs">Tên hàng</th><th className="px-2 py-1.5 text-right text-xs">SL hủy</th><th className="px-2 py-1.5 text-right text-xs">Giá vốn</th><th className="px-2 py-1.5 w-8"></th></tr></thead>
                <tbody>{items.map((item,idx)=>(<tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                  <td className="px-2 py-1"><Input className="text-xs h-7" value={item.product_code} onChange={e=>updateItem(idx,"product_code",e.target.value)} /></td>
                  <td className="px-2 py-1"><Input className="text-xs h-7" value={item.product_name} onChange={e=>updateItem(idx,"product_name",e.target.value)} placeholder="Tên sản phẩm" /></td>
                  <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.quantity} onChange={e=>updateItem(idx,"quantity",Number(e.target.value))} /></td>
                  <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.cost_price} onChange={e=>updateItem(idx,"cost_price",Number(e.target.value))} /></td>
                  <td className="px-2 py-1">{items.length>1&&<button onClick={()=>removeItem(idx)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>}</td>
                </tr>))}</tbody>
              </table>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4"><Button variant="outline" onClick={onClose}>Đóng</Button><Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-damage">{submitting?"Đang tạo...":"Tạo phiếu xuất hủy"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
